//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import moment from 'moment-timezone';

import {
  SamplingCampaignRequest,
  SamplingCampaignRequestReduced,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { Serialized } from '@utils/types/Serialized';

export const deserializeCampaignRequestReduced = (
  campaign: Serialized<SamplingCampaignRequestReduced>,
): SamplingCampaignRequestReduced => ({
  ...campaign,
  lastUpdated: campaign.lastUpdated
    ? new Date(campaign.lastUpdated)
    : undefined,
});

export const deserializeProduct = (
  product: Serialized<BVSamplingProduct>,
): BVSamplingProduct => ({
  ...product,
  // Convert shipping availability date from UTC timestamp to Date object
  // BV API provides dates in format "YYYY-MM-DD" which are converted to UTC timestamps
  // Using moment.utc() ensures consistent date interpretation across all timezones,
  // matching BV's backend processing where dates are stored in UTC
  shippingAvailabilityDate: product.shippingAvailabilityDate
    ? moment.utc(product.shippingAvailabilityDate).toDate()
    : undefined,
});

const deserializePurchaseOption = (
  option: Serialized<PurchaseOption>,
): PurchaseOption => ({
  ...option,
  expiryDate: option.expiryDate ? new Date(option.expiryDate) : undefined,
});

export const deserializeCampaignRequest = (
  campaign: Serialized<SamplingCampaignRequest>,
): SamplingCampaignRequest => {
  const deserializedCampaign: SamplingCampaignRequest = {
    ...campaign,
    lastUpdated: campaign.lastUpdated
      ? new Date(campaign.lastUpdated)
      : undefined,
    created: campaign.created ? new Date(campaign.created) : undefined,
    targetPublishDate: campaign.targetPublishDate
      ? new Date(campaign.targetPublishDate)
      : undefined,
    salesforceNotificationDate: campaign.salesforceNotificationDate
      ? new Date(campaign.salesforceNotificationDate)
      : undefined,
    products: campaign.products
      ? campaign.products.map(deserializeProduct)
      : undefined,
    purchaseOption: campaign.purchaseOption
      ? campaign.purchaseOption.map(deserializePurchaseOption)
      : undefined,
  };

  return deserializedCampaign;
};

const serializeProduct = (
  product: BVSamplingProduct,
): Serialized<BVSamplingProduct> => ({
  ...product,
  shippingAvailabilityDate: product.shippingAvailabilityDate
    ? product.shippingAvailabilityDate.getTime()
    : undefined,
});

const serializePurchaseOption = (
  option: PurchaseOption,
): Serialized<PurchaseOption> => ({
  ...option,
  expiryDate: option.expiryDate ? option.expiryDate.getTime() : undefined,
});

export const serializeCampaignRequest = (
  campaign: SamplingCampaignRequest,
): Serialized<SamplingCampaignRequest> => {
  const serializedCampaign: Serialized<SamplingCampaignRequest> = {
    ...campaign,
    lastUpdated: campaign.lastUpdated
      ? campaign.lastUpdated.getTime()
      : undefined,
    created: campaign.created ? campaign.created.getDate() : undefined,
    targetPublishDate: campaign.targetPublishDate
      ? campaign.targetPublishDate.getTime()
      : undefined,
    salesforceNotificationDate: campaign.salesforceNotificationDate
      ? campaign.salesforceNotificationDate.getTime()
      : undefined,
    products: campaign.products
      ? campaign.products.map(serializeProduct)
      : undefined,
    purchaseOption: campaign.purchaseOption
      ? campaign.purchaseOption.map(serializePurchaseOption)
      : undefined,
  };

  return serializedCampaign;
};
